import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Button, Form, Select, message, Spin, InputNumber, Flex, Image } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";

import {
	useGetProductByIdQuery,
	type GetProductByIdQueryHookResult as ProductByIdType
} from "graphql/queries/get-product-by-id.generated";
import { useUpdateProductByIdMutation } from "graphql/mutations/update-product-by-id.generated";
import { Products_Set_Input, Product_Attachments_Insert_Input } from "graphql/generated/graphql";
import { useAddProductAttachmentsMutation } from "graphql/mutations/add-attachments.generated";
import { useDeleteAttachmentByIdMutation } from "graphql/mutations/delete-attachment-by-id.generated";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";

import { ProductType, UserRoleValue } from "constants/field-values";
import routeNames from "constants/route-names";
import { pruneEmptyValues } from "utils/helpers";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { deleteFromStorage, getFileExtension, uploadToStorage } from "utils/files-management";
import { useGetProductCategoriesQuery } from "graphql/queries/get-product-categories.generated";

const { TextArea } = Input;

const ProductEdit: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams<{ deviceId: string }>();
	const { loggedUserValue } = useLoggedUser();
	const { updateSelectedAttachments } = useSelectedAttachments();
	const [name, setName] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [productNotes, setProductNotes] = useState<string>("");
	const [sku, setSku] = useState<string>("");
	const [type, setType] = useState<ProductType | string>("");
	const [model, setModel] = useState<string>("");
	const [make, setMake] = useState<string>("");
	const [productTypeOptions, setProductTypeOptions] = useState<{ value: string; label: string }[]>([]);
	const [retailPrice, setRetailPrice] = useState<number | null>(null);
	const [extendedPrice, setExtendedPrice] = useState<number | null>(null);
	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [existingAttachmentsFiles, setExistingAttachmentsFiles] = useState<
		Array<Product_Attachments_Insert_Input | any>
	>([]);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<Product_Attachments_Insert_Input | any>>([]);
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);
	const [productPhotoThumbUrl, setProductPhotoThumbUrl] = useState<string>("");
	const [productPhotoFile, setProductPhotoFile] = useState<RcFile | null>(null);

	const handleOnErrorGetProductById = useCallback((error: ApolloError) => {
		console.log("useGetProductByIdQuery Error: ", error);
	}, []);

	const handleOnCompleteGetProductById = useCallback((data: ProductByIdType["data"]) => {
		const {
			name,
			description,
			product_notes,
			sku,
			product_category,
			model,
			make,
			retail_price,
			product_attachments = [],
			photo_link
		} = data?.products_by_pk[0] || {};

		setName(name || "");
		setDescription(description || "");
		setProductNotes(product_notes || "");
		setSku(sku || "");
		setType(product_category?.name || "");
		setModel(model || "");
		setMake(make || "");
		setRetailPrice(retail_price || null);

		setProductPhotoThumbUrl(photo_link || "");

		if (product_attachments.length) {
			setExistingAttachmentsFiles(product_attachments);
		} else {
			setExistingAttachmentsFiles([]);
		}
	}, []);

	const { data: productData, loading: loadingProduct } = useGetProductByIdQuery({
		skip: !params.deviceId,
		variables: {
			id: params.deviceId,
			is_not_deleted: true
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetProductById,
		onCompleted: handleOnCompleteGetProductById
	});

	const handleOnCompleteUpdateProductMutation = useCallback((data: any) => {
		navigate(routeNames.ADMIN_DEVICES);
	}, []);

	const handleOnErrorUpdateProductMutation = useCallback((error: ApolloError) => {
		console.log("updateProductByIdMutation Error: ", error);
	}, []);

	const [updateProductByIdMutation, { loading }] = useUpdateProductByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateProductMutation,
		onCompleted: handleOnCompleteUpdateProductMutation,
		refetchQueries: ["getProductById", "getProducts"]
	});

	const handleOnCompleteAddProductAttachmentsMutation = useCallback((data: any) => {
		setAttachmentsFiles([]);
	}, []);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setProductPhotoThumbUrl(url);
		setProductPhotoFile(file);
	}, []);

	const handleOnErrorAddProductAttachmentsMutation = useCallback((error: ApolloError) => {
		console.log("handleOnErrorAddProductAttachmentsMutation Error: ", error);
	}, []);

	const [addProductAttachmentsMutation, { loading: loadingAddProductAttachments }] = useAddProductAttachmentsMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddProductAttachmentsMutation,
		onCompleted: handleOnCompleteAddProductAttachmentsMutation,
		refetchQueries: ["getProductById", "getProducts"]
	});

	const handleOnCompleteDeleteAttachmentByIdMutation = useCallback((data: any) => {
		console.log("Complete Delete Attachment data: ", data);
	}, []);

	const handleOnErrorDeleteAttachmentByIdMutation = useCallback((error: ApolloError) => {
		console.log("useDeleteAttachmentByIdMutation Error: ", error);
	}, []);

	const [deleteAttachmentByIdMutation, { loading: loadingDeleteAttachmentById }] = useDeleteAttachmentByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorDeleteAttachmentByIdMutation,
		onCompleted: handleOnCompleteDeleteAttachmentByIdMutation,
		refetchQueries: ["getProductById", "getProducts"]
	});

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveExistingAttachment = useCallback((attachmentFile: any) => {
		const { id } = attachmentFile;

		if (id) {
			deleteAttachmentByIdMutation({
				variables: {
					id: id
				}
			});
		}
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file } = attachmentFile;

			const newList = attachmentsFiles.filter(element => element.uid !== uid);

			setAttachmentsFiles(newList);
		},
		[attachmentsFiles]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			const { uid, type, description, file, name } = attachmentFile;

			updateSelectedAttachments(attachmentFile);

			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { type, uid } = attachmentFile;

		setAttachmentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);

			return filteredList.concat([attachmentFile]);
		});
	}, []);

	const uploadAttachments = useCallback(async () => {
		if (attachmentsFiles.length > 0) {
			try {
				const promises: Promise<Product_Attachments_Insert_Input | undefined>[] = [];

				attachmentsFiles.forEach((attachment, index) => {
					const { type, description, file, name } = attachment;

					const fileMediaType = file.type.split("/")[0];
					const uniqueID = uuidv4();

					let bucketName = "products-attachments/";
					let attachmentName = "";

					const fileExtension = getFileExtension(file.name);
					let fileName = name ? `${name}.${fileExtension}` : file.name;

					const newAttachment: Product_Attachments_Insert_Input = {
						type,
						description,
						name,
						mime_type: file.type,
						product_id: params.deviceId,
						photo_link: null,
						document_link: null,
						video_link: null,
						audio_link: null
					};

					switch (fileMediaType) {
						case "image":
							bucketName = bucketName + "photos";
							attachmentName = `attachment-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "video":
							bucketName = bucketName + "videos";
							attachmentName = `attachment-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						case "audio":
							bucketName = bucketName + "audios";
							attachmentName = `attachment-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
						default:
							bucketName = bucketName + "documents";
							attachmentName = `attachment-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
							break;
					}

					const uploadTask = async () => {
						try {
							const fileObj = file?.originFileObj;
							const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`);

							switch (fileMediaType) {
								case "image":
									newAttachment.photo_link = publicUrl;
									break;
								case "video":
									newAttachment.video_link = publicUrl;
									break;
								case "audio":
									newAttachment.audio_link = publicUrl;
									break;
								default:
									newAttachment.document_link = publicUrl;
									break;
							}

							return newAttachment;
						} catch (error) {
							console.log("Error when trying to upload the attachment file to firestore. Error: ", error);
						}
					};

					promises.push(uploadTask());
				});

				try {
					const promisesResults = await Promise.all(promises);

					const filteredPromisesResults = pruneEmptyValues(promisesResults) as Product_Attachments_Insert_Input[];

					addProductAttachmentsMutation({
						variables: {
							objects: filteredPromisesResults
						}
					});
				} catch (error) {
					console.log("Error trying to upload attachments: ", error);
				}
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			}
		}

		return;
	}, [attachmentsFiles, params.deviceId]);

	const handleOnRemovePhoto = useCallback(async () => {
		const { photo_link = "" } = productData?.products_by_pk[0] || {};

		// Extract and store the storage path from the URL
		if (photo_link) {
			// Firebase Storage URLs contain a token, we need to extract just the path

			try {
				await deleteFromStorage(photo_link)
			} catch (error) {
				console.error("Error deleting Device Photo from firebase:", error);
			}

			try {
				// Update property in database to remove the image URL
				await updateProductByIdMutation({
					variables: {
						set: {
							photo_link: null
						},
						id: params.deviceId
					}
				});

				// Clear local state
				setProductPhotoThumbUrl("");
				setProductPhotoFile(null);

				message.success("Device photo deleted successfully");
			} catch (error) {
				console.error("Error deleting Device photo:", error);
				message.error("Failed to delete Device photo");
			}
		}
	}, [productData, params.deviceId, updateProductByIdMutation]);

	const uploadProductImg = useCallback(async () => {
		if (productPhotoFile) {
			try {
				const uniqueID = uuidv4();

				const bucketName = "product-photos/";

				const fileName = `product-photo-${name}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${productPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(productPhotoFile, () => {}, `${bucketName}/${fileName}`);

				return publicUrl;
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
				return "";
			}
		}
	}, [productPhotoFile, name]);

	const handleOnSubmit = useCallback(async () => {
		if (params.deviceId) {
			if (!type) {
				message.error("Please select a device type.");
				return;
			}

			setUploading(true);

			try {
				try {
					let productInput: Products_Set_Input = {
						name,
						description,
						sku,
						type,
						model,
						make,
						retail_price: retailPrice,
						extended_price: extendedPrice,
						product_notes: productNotes
					};

					let photoLink;
					if (productPhotoFile) {
						photoLink = await uploadProductImg();
						productInput.photo_link = photoLink;
					}

					updateProductByIdMutation({
						variables: {
							set: productInput,
							id: params.deviceId
						}
					});
				} catch (error) {
					console.log("Error on submit product update. Error: ", error);
				}

				if (attachmentsFiles.length) {
					await uploadAttachments();
				}
			} catch (error) {
				console.log("Error on submit product update. Error: ", error);
			} finally {
				setUploading(false);
				const path = `${routeNames.ADMIN_DEVICES_DETAILS.replace(":deviceId", params.deviceId)}`;

				navigate(path);
			}
		}
	}, [
		params.deviceId,
		attachmentsFiles,
		name,
		description,
		sku,
		type,
		model,
		make,
		retailPrice,
		extendedPrice,
		productNotes,
		loggedUserValue,
		productPhotoFile,
		uploadAttachments,
		uploadProductImg
	]);

	const { loading: loadingProductTypes } = useGetProductCategoriesQuery({
		fetchPolicy: "network-only",
		onCompleted: data => {
			setProductTypeOptions(data.product_categories.map(product => ({ value: product.name, label: product.label })));
		}
	});

	useEffect(() => {
		const canUserEdit = [
			UserRoleValue.admin_user,
			UserRoleValue.vendor_manager,
			UserRoleValue.vendor_employee
		].includes(loggedUserValue.role as UserRoleValue);
		if (loggedUserValue && !canUserEdit) {
			const path = `${routeNames.INSTALLED_DEVICE_DETAILS.replace(":deviceId", params.deviceId!)}`;

			navigate(path);
		}
	}, [loggedUserValue, navigate, params.deviceId]);

	const isLoading =
		uploading ||
		loading ||
		loadingDeleteAttachmentById ||
		loadingProduct ||
		loadingAddProductAttachments ||
		loadingProductTypes;

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Edit Device"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Device Detail"}</span>
				{loadingProduct ? (
					<Flex
						style={{
							height: "80%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center"
						}}
					>
						<Spin />
					</Flex>
				) : (
					<>
						<Form.Item
							className="product-edit-form-item"
							rules={[{ required: true, message: "Please input the device name." }]}
							required
							validateTrigger="onBlur"
						>
							<Input
								name="name"
								placeholder="Name"
								className="add-product-input-pair"
								value={name}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									setName(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<div className="two-inputs-container">
							<Form.Item
								className="product-edit-form-item-pair"
								rules={[
									{
										required: true,
										message: "Please input the make of the Device."
									}
								]}
								required
								validateTrigger="onBlur"
							>
								<Input
									name="make"
									placeholder="Make"
									className="add-product-input-pair"
									value={make}
									autoComplete="off"
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setMake(event.target.value);
									}}
									allowClear
									disabled={isLoading}
								/>
							</Form.Item>

							<Form.Item
								className="product-edit-form-item-pair"
								rules={[
									{
										required: true,
										message: "Please input the model of the Device."
									}
								]}
								required
								validateTrigger="onBlur"
							>
								<Input
									name="model"
									placeholder="Model"
									className="add-product-input-pair"
									value={model}
									autoComplete="off"
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setModel(event.target.value);
									}}
									allowClear
									disabled={isLoading}
								/>
							</Form.Item>
						</div>

						<div className="two-inputs-container">
							<Form.Item
								className="product-edit-form-item-pair"
								rules={[
									{
										required: true,
										message: "Please input the SKU of the Device."
									}
								]}
								required
								validateTrigger="onBlur"
							>
								<Input
									name="sku"
									placeholder="SKU"
									className="add-product-input-pair"
									value={sku}
									autoComplete="off"
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setSku(event.target.value);
									}}
									allowClear
									disabled={isLoading}
								/>
							</Form.Item>

							<Form.Item
								className="product-edit-form-item-pair"
								// rules and required doesn't seem to work
								rules={[{ required: true, message: "Please select a Device Type." }]}
								required
								validateTrigger="onBlur"
							>
								<Select
									value={type}
									className="add-product-input-select"
									onChange={value => setType(value)}
									options={productTypeOptions}
									placeholder="Device Type"
									allowClear
									disabled={isLoading}
									suffixIcon={
										<img
											src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
											alt="dropdown icon"
											className="chevron-button"
										/>
									}
								/>
							</Form.Item>
						</div>

						<Form.Item className="product-edit-form-item-pair">
							<InputNumber
								name="retailPrice"
								placeholder="Retail Price"
								className="add-product-input-pair"
								value={retailPrice}
								disabled={isLoading}
								onChange={value => {
									setRetailPrice(value);
								}}
							/>
						</Form.Item>

						<Form.Item className="product-edit-form-item">
							<TextArea
								name="description"
								placeholder="Description"
								style={{ height: "148px" }}
								value={description}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
									setDescription(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<Form.Item className="product-edit-form-item">
							<TextArea
								name="product-notes"
								placeholder="Device Notes"
								style={{ height: "137px" }}
								value={productNotes}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
									setProductNotes(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<Button className="upload-product-button" onClick={() => setAddProductPhotoModalOpen(true)}>
							Upload Device Image
						</Button>

						{productPhotoThumbUrl ? (
							<div className="organization-button-container">
								<Image width={170} src={productPhotoThumbUrl} />{" "}
								<img
									alt={`remove-organization-photo-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
									className="organization-photo-action-icon"
									onClick={() => {
										if (!isLoading) {
											handleOnRemovePhoto();
										}
									}}
								/>
							</div>
						) : null}

						<UploadPhotoModal
							open={addProductPhotoModalOpen}
							onCancel={() => setAddProductPhotoModalOpen(false)}
							uploadMethod={handleOnConfirmPhoto}
							disableCrop
						/>
					</>
				)}
			</Form>

			<div className="add-product-form">
				<span className="add-product-form-title">{"Device Resources"}</span>

				<div className="add-product-documents">
					<Button
						className="upload-product-button"
						onClick={() => setAddAttachmentModalOpen(true)}
						disabled={isLoading}
					>
						Add Resources
					</Button>
				</div>

				{existingAttachmentsFiles && existingAttachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{existingAttachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveExistingAttachment(attachmentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditAttachment(attachmentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}

				{attachmentsFiles && attachmentsFiles.length > 0 ? (
					<div className="attachments-container ">
						{attachmentsFiles.map(attachmentFile => {
							const uid = attachmentFile.uid || attachmentFile.id;

							return (
								<AttachmentItem
									key={`product-attachment-item-${uid}`}
									name={attachmentFile.name}
									file={attachmentFile.file}
									type={attachmentFile.type}
									onRemove={() => {
										if (!isLoading) {
											handleOnRemoveAttachment(attachmentFile);
										}
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) {
											handleOnEditAttachment(attachmentFile);
										}
									}}
								/>
							);
						})}
					</div>
				) : null}
			</div>

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit}>
					Save
				</Button>
			</div>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={true}
			/>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default ProductEdit;
